import { Link } from "react-router-dom";
import { TopBar } from "../components/Layout";

interface Cred {
  key: string;
  vendor: string;
  url: string;
  urlLabel: string;
  why: string;
  setOn: string;
  value?: string;
}

/** Platform-level secrets — set by ActionConnect staff via `wrangler secret put` on each Worker. */
const PLATFORM: Cred[] = [
  {
    key: "CF_ACCOUNT_ID",
    vendor: "Cloudflare",
    url: "https://dash.cloudflare.com",
    urlLabel: "dash.cloudflare.com",
    why: "Identifies the account when calling the Cloudflare REST / D1 HTTP API to reach per-tenant D1 databases and provision resources.",
    setOn: "ac-edge, ac-portal, ac-email-in, ac-email-out, ac-notifications, ac-provisioner",
    value: "55157df5ce6c804625f87d7699aa909d",
  },
  {
    key: "CF_API_TOKEN",
    vendor: "Cloudflare",
    url: "https://dash.cloudflare.com/profile/api-tokens",
    urlLabel: "dash → My Profile → API Tokens",
    why: "Authorizes runtime calls to the D1 HTTP query API (per-tenant DBs) and all provisioning calls. Needs D1 write, Workers KV write, DNS edit, and account/zone read.",
    setOn: "all six workers above (ac-provisioner uses the broadest scope)",
  },
  {
    key: "CF_KV_NAMESPACE_ID",
    vendor: "Cloudflare",
    url: "https://dash.cloudflare.com",
    urlLabel: "dash → Workers & Pages → KV (or `wrangler kv namespace list`)",
    why: "The ac-routing KV namespace the provisioner writes host→tenant routing records into over the REST API.",
    setOn: "ac-provisioner",
    value: "eda6228c51924786b8b0277a7cb606df",
  },
  {
    key: "CF_ZONE_ID",
    vendor: "Cloudflare",
    url: "https://dash.cloudflare.com",
    urlLabel: "dash → actionconnect.com → Overview (API)",
    why: "The actionconnect.com zone id, used to add per-tenant subdomains / custom hostnames and DNS records during provisioning.",
    setOn: "ac-provisioner",
  },
  {
    key: "MAILGUN_API_KEY",
    vendor: "Mailgun",
    url: "https://app.mailgun.com/settings/api_security",
    urlLabel: "app.mailgun.com → Settings → API Keys",
    why: "Authenticates outbound email sends — notifications, ticket replies, magic-link and contract e-sign emails.",
    setOn: "ac-email-out",
  },
  {
    key: "MAILGUN_DOMAIN",
    vendor: "Mailgun",
    url: "https://app.mailgun.com/app/sending/domains",
    urlLabel: "app.mailgun.com → Sending → Domains",
    why: "The verified sending domain (DKIM/SPF configured) that outbound mail is sent from.",
    setOn: "ac-email-out",
  },
  {
    key: "DIRECTORY_KEK",
    vendor: "Self-generated (no vendor)",
    url: "https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues",
    urlLabel: "generate locally — randomBytes(32) → base64",
    why: "Base64 32-byte key-encryption-key that envelope-encrypts each tenant's Entra client secret (AES-256-GCM) before it is stored. Plaintext credentials never touch the DB.",
    setOn: "ac-edge",
  },
  {
    key: "ADMIN_API_TOKEN",
    vendor: "Self-generated (interim)",
    url: "https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues",
    urlLabel: "generate locally — random secret",
    why: "Gates the platform admin API / this console until full staff SSO is wired in.",
    setOn: "ac-admin",
  },
];

/** Per-tenant credentials — entered by each tenant's admin; secrets are stored encrypted, not by staff. */
const PER_TENANT: Cred[] = [
  {
    key: "Entra directory connection",
    vendor: "Microsoft Entra ID",
    url: "https://entra.microsoft.com",
    urlLabel: "entra.microsoft.com → App registrations",
    why: "Directory (tenant) id + App (client) id + client secret, with admin-consented application permission User.Read.All (plus GroupMember.Read.All if group-scoped). Powers directory sync — importing/reconciling the tenant's users from their Entra via Microsoft Graph. The client secret is stored AES-256-GCM-encrypted under DIRECTORY_KEK.",
    setOn: "Tenant app → Settings → Directory",
  },
  {
    key: "Tenant SSO connection",
    vendor: "Tenant's IdP (Entra / Google / Okta…)",
    url: "https://www.rfc-editor.org/rfc/rfc6749",
    urlLabel: "tenant's IdP admin console",
    why: "OIDC issuer + client id (client secret referenced as a Worker secret) or SAML metadata, so the tenant's agents sign in via their own IdP with JIT provisioning. Plan-gated (features.sso).",
    setOn: "Tenant app → Settings → SSO",
  },
];

function CredTable({ rows }: { rows: Cred[] }) {
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Key / credential</th>
          <th>Vendor &amp; where to get it</th>
          <th>Why it&apos;s needed</th>
          <th>Set on</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td>
              <code>{r.key}</code>
              {r.value && (
                <div className="faint" style={{ fontSize: "0.74rem", marginTop: 4 }}>
                  known: <span className="mono">{r.value}</span>
                </div>
              )}
            </td>
            <td>
              <strong>{r.vendor}</strong>
              <div style={{ fontSize: "0.78rem", marginTop: 2 }}>
                <a href={r.url} target="_blank" rel="noreferrer">
                  {r.urlLabel}
                </a>
              </div>
            </td>
            <td style={{ fontSize: "0.84rem" }}>{r.why}</td>
            <td className="mono" style={{ fontSize: "0.76rem" }}>
              {r.setOn}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ResourceApiKeysPage() {
  return (
    <>
      <TopBar crumbs={<><Link to="/resources">Resources</Link> / API keys &amp; vendors</>} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>API keys &amp; vendors</h1>
            <div className="sub">
              Every external credential ActionConnect depends on — the vendor, where to get it, and why
              it&apos;s needed.
            </div>
          </div>
        </div>

        <div className="alert info">
          Platform secrets are set with <code>wrangler secret put &lt;NAME&gt; --env=&quot;&quot;</code> from
          the relevant worker directory — never committed to code or wrangler files. Per-tenant credentials are
          entered by each tenant&apos;s own admin and stored encrypted; staff do not hold them.
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Platform secrets (staff-set, per worker)</h2>
          </div>
          <div className="card-body tight">
            <CredTable rows={PLATFORM} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Per-tenant credentials (tenant-provided)</h2>
          </div>
          <div className="card-body tight">
            <CredTable rows={PER_TENANT} />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Not yet integrated</h2>
          </div>
          <div className="card-body">
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
              <li>
                <strong>Staff IdP (admin SSO)</strong> — Google Workspace / Entra / WorkOS to replace the interim{" "}
                <code>ADMIN_API_TOKEN</code> gate on this console. Vendor TBD.
              </li>
              <li>
                <strong>Payment gateway</strong> — none today; billing is manual / staff-billed. A{" "}
                <code>BillingProvider</code> seam exists for a future gateway (e.g. Stripe).
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
